<template>
    <div class="action-dropdown" v-if="tripId || stopId || shipmentId || orderId">
        <div class="recalculate-button" :class="[{ 'sync-loader': isRecalculating || externalRecalculate }]">
            <md-button
                v-if="!isRecalculating && !externalRecalculate"
                @click="recalculateRates()"
                class="btn-blue md-just-icon md-round"
                :class="[{ 'btn-disabled': enableRecalculate }]"
                :disabled="enableRecalculate"
                title="Recalculate Rates"
            >
                <md-icon>currency_exchange</md-icon>
            </md-button>
            <div v-else class="btn-blue md-button">
                <div id="loading"></div>
            </div>
        </div>

        <div v-if="isAnyApprovalFeatureEnabled" class="approval-buttons">
            <md-button
                v-if="!disableApprovalButton"
                @click="approveJobs()"
                class="btn-green md-just-icon md-round"
                :class="[{ 'btn-disabled': disableApprovalButton }]"
                :disabled="disableApprovalButton"
                title="Approve"
            >
                <md-icon>thumb_up</md-icon>
            </md-button>

            <md-button
                v-else
                @click="unapproveJobs()"
                class="btn-red md-just-icon md-round"
                :class="[{ 'btn-disabled': disableUnapproveButton }]"
                :disabled="disableUnapproveButton"
                title="Unapprove"
            >
                <md-icon>thumb_down</md-icon>
            </md-button>
        </div>

        <div>
            <md-button
                v-if="stopId == null"
                @click="openUpdateTimeModal()"
                class="btn-blue md-just-icon md-round"
                title="Update scheduled times"
                :class="[{ 'btn-disabled': disableUpdateTimeButton }]"
                :disabled="disableUpdateTimeButton"
            >
                <md-icon>access_time</md-icon>
            </md-button>
            <md-button
                @click="addServicePackage()"
                class="md-just-icon md-round"
                :class="[
                    {
                        'btn-disabled': disableServicePackageButton
                    },
                    { 'btn-orange': selectedRateGroupId != null },
                    { 'btn-blue': selectedRateGroupId == null }
                ]"
                :disabled="disableServicePackageButton"
                title="Add/Update Service Package"
            >
                <md-icon>{{ selectedRateGroupId ? 'store_mall_directory' : 'add_business' }}</md-icon>
            </md-button>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins';
import { FeatureManager } from '@/directives';
import { FEATURE_NAMES } from '@/utils/constants';
import UpdateJobTimeModalNew from './UpdateJobTimeModal';
import UpdateServicePackageModal from './UpdateServicePackageModal';

export default {
    name: 'ActionRendererProcessCharges',
    mixins: [GeneralMixin, FeatureManager],
    data() {
        return {
            type: null,
            tripId: null,
            stopId: null,
            shipmentId: null,
            orderId: null,
            reference: null,
            rateGroupNameCosts: null,
            rateGroupNameCharges: null,
            rateGroupCostId: null,
            rateGroupChargeId: null,
            teamId: null,
            actualStartTime: null,
            actualEndTime: null,
            scheduledStartTime: null,
            scheduledEndTime: null,

            isRecalculating: false,
            isCostsApproved: false,
            isChargesApproved: false,
            customerInvoiceId: null,
            teamMemberInvoiceId: null,
            isCostApprovalFeatureEnabled: this.$_hasFeature(FEATURE_NAMES.JobsCostApproval),
            isChargeApprovalFeatureEnabled: this.$_hasFeature(FEATURE_NAMES.JobsChargeApproval)
        };
    },
    beforeMount() {
        this.updateData(this.params);
    },
    computed: {
        selectedRateGroupId() {
            return this.params.rateGroupChargeId || this.params.rateGroupCostId;
        },
        enableRecalculate() {
            return this.params.enableRecalculate;
        },
        externalRecalculate() {
            return this.params.externalRecalculate;
        },
        isAnyApprovalFeatureEnabled() {
            return this.isCostApprovalFeatureEnabled || this.isChargeApprovalFeatureEnabled;
        },
        disableApprovalButton() {
            const needsCostApproval = this.isCostApprovalFeatureEnabled && this.isCostsApproved;
            const needsChargeApproval = this.isChargeApprovalFeatureEnabled && this.isChargesApproved;

            return needsCostApproval || needsChargeApproval;
        },
        disableUnapproveButton() {
            if (
                this.isCostApprovalFeatureEnabled &&
                this.isChargeApprovalFeatureEnabled
            ) {
                if(!this.teamMemberInvoiceId && !this.customerInvoiceId) {
                    return !this.isCostsApproved || !this.isChargesApproved;
                }

                return true;
            }
            if (this.isCostApprovalFeatureEnabled && !this.teamMemberInvoiceId) {
                return !this.isCostsApproved;
            }
            if (this.isChargeApprovalFeatureEnabled && !this.customerInvoiceId) {
                return !this.isChargesApproved;
            }

            return true;
        },
        disableUpdateTimeButton() {
            return !this.tripId && !this.shipmentId;
        },
        disableServicePackageButton() {
            return this.customerInvoiceId != null ||
                            this.teamMemberInvoiceId != null||
                            this.isCostsApproved ||
                            this.isChargesApproved ||
                            (this.tripRateGroupId == null && this.userRateGroupId != null && this.customerDefaultRateGroupId != null) ||
                            this.orderId != null;;
        }
    },
    methods: {
        updateData(params) {
            const propKeys = [
                'reference',
                'type',
                'tripId',
                'stopId',
                'shipmentId',
                'orderId',
                'rateGroupId',
                'teamId',
                'customerInvoiceId',
                'teamMemberInvoiceId',
                'isCostsApproved',
                'isChargesApproved',
                'rateGroupNameCharges',
                'rateGroupNameCosts',
                'rateGroupChargeId',
                'rateGroupCostId',
                'actualStartTime',
                'actualEndTime',
                'scheduledStartTime',
                'scheduledEndTime'
            ];

            // eslint-disable-next-line no-unused-expressions
            propKeys?.forEach((key) => {
                if (key in params) {
                    this[key] = params[key];
                }
            });
        },
        async recalculateRates() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Recalculate',
                    body: `Are you sure you want to recalculate ${this.reference}?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.isRecalculating = true;

                        if (!this.validAction()) {
                            return;
                        }

                        if (this.rateGroupChargeId || this.rateGroupCostId) {
                            this.isRecalculating = true;
                        }

                        const payload = {
                            method: 'post',
                            data: this.getToUpdateData()
                        };

                        const api = `/api/rates/recalculate-rates`;
                        try {
                            const response = await handleRequests(api, payload);

                            if (response !== null && response.data !== null && response.status === 200) {
                                this.isRecalculating = false;
                            }
                        } catch (e) {
                            const message = `Cannot recalculate, please contact support if issue persists`;
                            showErrorMessage(this, message, e);
                        }
                    }

                    this.isRecalculating = false;
                });
        },
        async approveJobs() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Approve',
                    body: `Are you sure you want to approve ${this.reference}?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    if (!this.validAction()) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data: this.getToUpdateData()
                    };

                    this.$_handleLoaderState(true, 'APPROVING...');

                    const api = `/api/process-charges/approve-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess(`Job ${this.reference} approved.`);
                        this.$_handleLoaderState(false);
                    } catch (e) {
                        const message = `Cannot approve, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                        this.$_handleLoaderState(false);
                    }
                });
        },
        async unapproveJobs() {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Unapprove',
                    body: `Are you sure you want to unapprove ${this.reference}?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    if (!this.validAction()) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data: this.getToUpdateData()
                    };

                    this.$_handleLoaderState(true, 'UNAPPROVING...');

                    const api = `/api/process-charges/unapprove-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess(`Job ${this.reference} unapproved.`);
                        this.$_handleLoaderState(false);
                    } catch (e) {
                        const message = `Cannot unapprove, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                        this.$_handleLoaderState(false);
                    }
                });
        },
        openUpdateTimeModal() {
            // only open if the job is a trip
            if (!this.tripId && !this.shipmentId) {
                return;
            }
            let times = {};

            if (this.tripId || this.shipmentId) {
                times = {
                    startTime: this.actualStartTime,
                    endTime: this.actualEndTime
                };
            } else {
                times = {
                    endTime: this.actualEndTime
                };
            }
            let jobType = '';
            if (this.tripId) {
                jobType = 'trip';
            } else if (this.shipmentId) {
                jobType = 'shipment';
            } else {
                jobType = 'stop';
            }
            this.$modal
                .show(UpdateJobTimeModalNew, {
                    tripId: this.tripId,
                    jobType,
                    initialStartTime: times.startTime,
                    initialEndTime: times.endTime,
                    shipmentId: this.shipmentId
                })
                .then((response) => {
                    if (response && response?.code.toLowerCase() === 'ok') {
                        this.params.fetchData(this.shipmentId || this.tripId);
                        this.$modal.hide();
                    }
                });
        },
        getToUpdateData() {
            const toUpdate = {
                tripIds: this.type === 'trip' ? [this.tripId] : [],
                stopIds: this.type === 'stop' ? [this.stopId] : [],
                shipmentIds: this.type === 'shipment' ? [this.shipmentId] : [],
                orderIds: this.type === 'order' ? [this.orderId] : [],
                teamId: this.teamId
            };

            return toUpdate;
        },
        validAction() {
            if (this.type === 'trip' && (this.tripId == null || this.tripId <= 0)) {
                return false;
            }
            if (this.type === 'stop' && (this.stopId == null || this.stopId <= 0)) {
                return false;
            }
            if (this.type === 'shipment' && (this.shipmentId == null || this.shipmentId <= 0)) {
                return false;
            }
            if (this.type === 'order' && (this.orderId == null || this.orderId <= 0)) {
                return false;
            }

            return true;
        },
        addServicePackage() {
            this.$modal
                .show(UpdateServicePackageModal, {
                    rateGroupId: this.selectedRateGroupId,
                    jobType: this.type,
                    tripIds: this.tripId != null ? [this.tripId] : [],
                    stopIds: this.stopId != null ? [this.stopId] : [],
                    shipmentIds: this.shipmentId != null ? [this.shipmentId] : [],
                    orderIds: this.orderId ? [this.orderId] : []
                })
                .then((response) => {
                    if (response) {
                        this.params.triggerRecalculate(this.tripId || this.stopId || this.shipmentId || this.orderId);
                        this.$modal.hide();
                    }
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.action-dropdown {
    ::v-deep .md-button {
        width: 30px;
        background-color: white !important;
        border: 1px solid #000;
        height: 30px;
        vertical-align: middle;
        min-width: 30px;

        .md-button-content i {
            color: black !important;
        }
    }

    ::v-deep .md-ripple {
        padding: 10px !important;
    }
}

::v-deep .md-list-item-button {
    .md-icon {
        margin-right: 5px !important;
    }

    &:hover {
        .md-icon {
            color: #fff !important;
        }
    }

    span {
        padding: 0 !important;
        left: 50px !important;
        top: 14px;
    }

    .md-ripple {
        padding: 5px 12px !important;
    }
}

::v-deep .custom-spinner circle {
    fill: #aaa;
}

.sync-loader {
    vertical-align: top;
    .md-button {
        border-radius: 50%;
    }
}

#loading {
    display: inline-block;
    vertical-align: middle;
    width: 20px;
    height: 20px;
    border: 3px solid rgba(255, 255, 255, 1);
    border-radius: 50%;
    border-top-color: transparent;
    animation: spin 1s ease-in-out infinite;
    -webkit-animation: spin 1s ease-in-out infinite;
    margin: 4px;
}

@keyframes spin {
    to {
        -webkit-transform: rotate(360deg);
    }
}
@-webkit-keyframes spin {
    to {
        -webkit-transform: rotate(360deg);
    }
}

.action-dropdown > div {
    display: inline-flex;

    .md-button {
        margin-right: 5px;
    }
    .md-button:last-child {
        margin-right: 0;
    }

    .btn-blue {
        background-color: #2b93ff !important;
        border: 1px solid #2b93ff !important;

        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-green {
        background-color: #1db51d !important;
        border: 1px solid #1db51d !important;

        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-red {
        background-color: #ff0600 !important;
        border: 1px solid #ff0600 !important;

        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-orange {
        background-color: #ff9800 !important;
        border: 1px solid #ff9800 !important;
        .md-button-content i {
            color: #fff !important;
        }
    }

    .btn-disabled {
        background-color: #d3d3d3 !important;
        border: 1px solid #d3d3d3 !important;
        .md-button-content i {
            color: #969696 !important;
        }
    }
}

.action-dropdown .recalculate-button {
    .md-button {
        margin-right: 0;
    }
}
</style>
